<template>
  <div class="keypad" role="dialog" aria-label="Keypad">
    <div class="head">
      <span class="eyebrow">Keypad</span>
      <span class="sent" role="status" aria-live="polite">
        <template v-if="sentSequences.length">
          Sent this call: <span class="sentDigits">{{ sentSequences.join(' · ') }}</span>
        </template>
        <template v-else>Nothing sent yet</template>
      </span>
    </div>

    <!-- Typed digits are staged and previewed; only Enter/Send puts them on the
         line. Therefore, a wrong typed number is visible, not audible. -->
    <form class="stage" @submit.prevent="sendStaged">
      <input
        ref="inputEl"
        class="stageInput"
        type="text"
        inputmode="tel"
        autocomplete="off"
        maxlength="20"
        aria-label="Digits to send"
        placeholder="Extension or menu option"
        :disabled="!connected"
        :value="staged"
        @input="onInput"
      />
      <button class="sendBtn" type="submit" :disabled="!connected || !staged" @mousedown.prevent>
        Send
      </button>
    </form>

    <div class="keys">
      <button
        v-for="k in KEYS"
        :key="k.digit"
        type="button"
        class="key"
        :aria-label="`Send ${k.digit}`"
        :disabled="!connected"
        @mousedown.prevent
        @click="emit('send', k.digit)"
      >
        <span class="keyDigit">{{ k.digit }}</span>
        <span class="keyLetters">{{ k.letters }}</span>
      </button>
    </div>

    <p class="hint">Enter sends · Esc closes</p>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

defineProps<{
  connected: boolean
  sentSequences: readonly string[]
}>()
const emit = defineEmits<{ (e: 'send', seq: string): void }>()

const KEYS = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '' },
  { digit: '#', letters: '' },
]

const inputEl = ref<HTMLInputElement | null>(null)
const staged = ref('')

function onInput(e: Event): void {
  const el = e.target as HTMLInputElement
  const clean = el.value.replace(/[^0-9*#]/g, '')
  // Vue won't re-patch an unchanged binding, so a stripped letter is removed by hand.
  if (el.value !== clean) el.value = clean
  staged.value = clean
}

function sendStaged(): void {
  if (!staged.value) return
  emit('send', staged.value)
  staged.value = ''
}

function focus(): void {
  inputEl.value?.focus({ preventScroll: true })
}
defineExpose({ focus })
</script>

<style scoped>
.keypad { width: 252px; padding: var(--space-3); display: flex; flex-direction: column; gap: var(--space-3); background: var(--color-bg-surface); border: 1px solid var(--color-border-subtle); border-radius: var(--radius-lg); box-shadow: var(--shadow-md, 0 8px 24px rgba(0, 0, 0, 0.12)); }

.head { display: flex; flex-direction: column; gap: 2px; }
.eyebrow { font-size: var(--fs-xs); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-wide); text-transform: uppercase; color: var(--color-text-tertiary); }
.sent { font-size: var(--fs-xs); color: var(--color-text-secondary); min-height: 1.4em; }
.sentDigits { font-family: var(--font-mono); font-variant-numeric: tabular-nums; color: var(--color-text-primary); }

.stage { display: flex; gap: var(--space-2); }
.stageInput { flex: 1; min-width: 0; height: 34px; padding: 0 var(--space-3); border: 1px solid var(--color-border-subtle); border-radius: var(--radius-md); background: var(--color-bg-app); font-family: var(--font-mono); font-size: var(--fs-sm); font-variant-numeric: tabular-nums; letter-spacing: 0.08em; color: var(--color-text-primary); outline: none; }
.stageInput::placeholder { font-family: var(--font-sans); letter-spacing: normal; color: var(--color-text-tertiary); }
.stageInput:focus { border-color: var(--color-accent); box-shadow: var(--shadow-focus); }
.sendBtn { height: 34px; padding: 0 var(--space-4); border: 0; border-radius: var(--radius-full); background: var(--color-bg-app); box-shadow: var(--shadow-neu-in-cream); font-family: var(--font-sans); font-size: var(--fs-sm); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-tight); color: var(--color-text-secondary); cursor: pointer; transition: background var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out); }
.sendBtn:hover:not(:disabled) { background: var(--color-bg-subtle); color: var(--color-text-primary); }
.sendBtn:active:not(:disabled) { transform: scale(0.97); }
.sendBtn:disabled { opacity: 0.5; cursor: not-allowed; }

.keys { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--space-2); }
.key { height: 44px; border: 0; border-radius: var(--radius-md); background: var(--color-bg-app); box-shadow: var(--shadow-neu-in-cream); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; font-family: var(--font-sans); color: var(--color-text-primary); cursor: pointer; transition: background var(--duration-fast) var(--ease-out); }
.key:hover:not(:disabled) { background: var(--color-bg-subtle); }
.key:active:not(:disabled) { transform: scale(0.96); }
.key:disabled { opacity: 0.5; cursor: not-allowed; }
.keyDigit { font-size: var(--fs-base); font-weight: var(--fw-semibold); font-variant-numeric: tabular-nums; line-height: 1; }
.keyLetters { font-size: 9px; font-weight: var(--fw-semibold); letter-spacing: var(--tracking-wide); color: var(--color-text-tertiary); line-height: 1; min-height: 9px; }

.hint { margin: 0; font-size: var(--fs-xs); color: var(--color-text-tertiary); text-align: center; }
</style>
